import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, CheckCircle2, Clock, Ban } from "lucide-react";
import toast from "react-hot-toast";
import PieChart, { PieChartDatum } from "./PieChart";
import MetricsStrip from "./MetricsStrip";
import { STATUS_COLORS } from "./statusColors";
import { HospitalStats } from "../../services/superAdmin.service";
import { getRole } from "../../utils/auth";
import ROLES from "../../utils/constants";
import { OverviewCardSkeleton } from "../common/Skeleton";
import WidgetErrorState from "../common/WidgetErrorState";
import EmptyState from "../common/EmptyState";
import StaleDataBanner from "../common/StaleDataBanner";

export interface HospitalOverviewProps {
  stats: HospitalStats | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  isStale?: boolean;
}

export const HospitalOverview = ({
  stats,
  loading,
  error,
  onRetry,
  isStale = false,
}: HospitalOverviewProps) => {
  const navigate = useNavigate();

  const goToHospitals = (params?: { status?: string; onboardingStatus?: string }) => {
    const role = getRole();
    if (role !== ROLES.SUPER_ADMIN) {
      toast.error("You do not have permission to view hospitals");
      return;
    }
    const search = new URLSearchParams();
    if (params?.status) search.set("status", params.status);
    if (params?.onboardingStatus) search.set("onboardingStatus", params.onboardingStatus);
    const qs = search.toString();
    navigate(qs ? `/hospitals?${qs}` : "/hospitals");
  };

  if (loading && !stats) {
    return <OverviewCardSkeleton />;
  }

  if (error && !stats) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E4EAF0] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E4EAF0] flex items-center justify-between bg-gray-50/30">
          <h2 className="text-lg font-bold text-[#10253A] flex items-center gap-2.5">
            <Building2 className="w-5 h-5 text-primary-500" aria-hidden="true" />
            Hospital Overview
          </h2>
        </div>
        <WidgetErrorState
          title="Hospital Statistics Unavailable"
          message={error}
          onRetry={onRetry}
          variant="card"
          className="border-0 shadow-none"
        />
      </div>
    );
  }

  const hospitalPieData: PieChartDatum[] = stats
    ? [
        { label: "Active", value: stats.active, color: STATUS_COLORS.active },
        { label: "In Progress", value: stats.inProgress, color: STATUS_COLORS.inProgress },
        { label: "Inactive", value: stats.inactive, color: STATUS_COLORS.inactive },
      ]
    : [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E4EAF0] overflow-hidden">
      <div className="px-6 py-4 border-b border-[#E4EAF0] flex items-center justify-between bg-gray-50/30">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-[#10253A] flex items-center gap-2.5">
            <Building2 className="w-5 h-5 text-primary-500" aria-hidden="true" />
            Hospital Overview
          </h2>
        </div>
        {loading && (
          <span className="text-xs text-[#63788E] flex items-center gap-1.5 animate-pulse">
            <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
            Updating...
          </span>
        )}
      </div>

      {isStale && (
        <div className="p-4 border-b border-[#E4EAF0]">
          <StaleDataBanner
            message="Unable to refresh latest hospital statistics. Showing cached numbers."
            onRetry={onRetry}
          />
        </div>
      )}

      {stats ? (
        <div className="p-6 space-y-6">
          <MetricsStrip
            items={[
              {
                key: "total",
                label: "Total Hospitals",
                value: stats.total,
                icon: <Building2 size={14} aria-hidden="true" /> as ReactNode,
                color: STATUS_COLORS.active,
                onClick: () => goToHospitals(),
              },
              {
                key: "active",
                label: "Active",
                value: stats.active,
                icon: <CheckCircle2 size={14} aria-hidden="true" /> as ReactNode,
                color: STATUS_COLORS.active,
                onClick: () => goToHospitals({ status: "ACTIVE" }),
              },
              {
                key: "in-progress",
                label: "In Progress",
                value: stats.inProgress,
                icon: <Clock size={14} aria-hidden="true" /> as ReactNode,
                color: STATUS_COLORS.inProgressText,
                onClick: () => goToHospitals({ onboardingStatus: "IN_PROGRESS" }),
              },
              {
                key: "inactive",
                label: "Inactive",
                value: stats.inactive,
                icon: <Ban size={14} aria-hidden="true" /> as ReactNode,
                color: STATUS_COLORS.inactive,
                onClick: () => goToHospitals({ status: "INACTIVE" }),
              },
            ]}
          />

          {stats.total === 0 ? (
            <EmptyState
              icon={<Building2 size={24} />}
              title="No hospitals registered yet"
              description="Get started by registering your first hospital to manage hospital profiles, departments, and doctors."
              action={{
                label: "Register Hospital",
                to: "/hospitals/new",
              }}
              variant="compact"
            />
          ) : (
            <div className="flex items-center justify-center">
              <PieChart data={hospitalPieData} centerLabel="Hospitals" emptyLabel="No data" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default HospitalOverview;
